import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Footer from "../../components/Layout/Footer";
import "../../assets/styles/AuthForm.css";
import { authService } from "../../services/authService";

const PASSWORD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^])[A-Za-z\d@$!#%*?&^]{8,}$/;

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successToast, setSuccessToast] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // 🔐 Token validation on load
    useEffect(() => {
        if (!token) {
            setError("Invalid or missing reset token");
        }
    }, [token]);

    const validateForm = (): boolean => {
        if (!newPassword || !confirmPassword) {
            setError("All fields are required");
            return false;
        }

        if (!PASSWORD_REGEX.test(newPassword)) {
            setError(
                "Password must be at least 8 characters long and include uppercase, lowercase, number and special character"
            );
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!token || !validateForm()) return;

        try {
            setLoading(true);

            await authService.resetPassword({
                token,
                new_password: newPassword,
                confirm_password: confirmPassword,
            });

            setSuccessToast(true);

            // Redirect after success
            setTimeout(() => {
                navigate("/");
            }, 3000);

        } catch (err: unknown) {
            if (axios.isAxiosError(err)) {
                const data = err.response?.data as any;

                let message = // Custom message
                    data?.details?.[0]?.msg || // Wrapped errors
                    "Password reset failed";

                // 🔹 Clean "400:" prefix if present
                if (typeof message === "string" && message.startsWith("400:")) {
                    message = message.replace("400:", "").trim();
                }

                setError(message);
            } else {
                setError("Unexpected error occurred");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="auth-form-container">
                <div className="auth-logos">
                    <img
                        src="/images/data-captive-logo.png"
                        onClick={() => navigate("/")} 
                        style={{cursor:"pointer"}}
                        alt="DataCaptive"
                    />
                    {/* <img
                        src="/images/main-logo.png"
                        onClick={() => navigate("/")}
                        style={{cursor:"pointer"}}
                        alt="Main Logo"
                    /> */}
                </div>

                <div className="auth-form-wrapper">
                    <form className="auth-form" onSubmit={handleSubmit}>
                        <p className="forgot-password-description-header text-center">
                            Reset Password
                        </p>

                        <div className="form-group">
                            <label>New Password</label>
                            <div className="password-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                />
                                <span
                                    className="eye-icon"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? 'hide' : 'show'}
                                </span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Confirm New Password</label>

                            <div className="password-wrapper">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                />
                                <span
                                    className="eye-icon"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? 'hide' : 'show'}
                                </span>
                            </div>
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? "Resetting..." : "Change Password"}
                        </button>
                    </form>
                </div>

                {successToast && (
                    <div className="toast-overlay">
                        <div className="toast-box">
                            <div className="toast-icon">✓</div>
                            <p className="toast-text">Password reset successfully.</p>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </>
    );
};

export default ResetPasswordPage;
